import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { getSeriesListApi } from "../../service/series";
import { useSeriesButtonPermissions } from "../../hooks/useSeriesButtonPermissions";
import { UserRoleManager } from "../../utils/userRole";
import { globalStore } from "../../utils/globalStore";
import type { HomeSeriesFolderItem } from "./types";
import { getHomeSeriesListPageSize } from "./homeSeriesGridLayout";

export type HomeTimeSortOption = "time" | "created" | "updated";
/** 管理员工具栏范围：团队 / 普通用户剧集（对应 ownerType 1 / 2），无「全部」 */
export type HomeScopeOption = "team" | "mine";

const getDefaultOwnerType = (): number => {
  const currentRole = UserRoleManager.getCurrentUserRole();
  if (currentRole?.isUser) {
    return 2;
  }
  return 1;
};

const mapListItemToFolder = (item: Record<string, unknown>): HomeSeriesFolderItem => {
  const sid = (item.seriesId ?? item.id) as number | string;
  const idNum = typeof sid === "number" ? sid : Number(sid);
  return {
    id: Number.isFinite(idNum) ? idNum : 0,
    name: String(item.name ?? ""),
  };
};

function parsePageQueryString(pageVal: string | null): number {
  const n = parseInt(pageVal || "1", 10);
  return Math.max(1, Number.isFinite(n) ? n : 1);
}

/**
 * 精品译制首页列表：getSeriesListApi；分页、防抖搜索、排序/范围参与请求。
 * 后端 sort：1 时间 2 名称；UI 上 created/updated 暂按时间请求 sort=1。
 */
export const useHomeSeriesSurface = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdminOrSuperAdmin } = useSeriesButtonPermissions();

  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [timeSort, setTimeSort] = useState<HomeTimeSortOption>("time");
  const [scope, setScope] = useState<HomeScopeOption>(() =>
    isAdminOrSuperAdmin() ? "team" : getDefaultOwnerType() === 2 ? "mine" : "team"
  );

  const [page, setPage] = useState(() => parsePageQueryString(searchParams.get("page")));
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== "undefined" ? getHomeSeriesListPageSize(window.innerWidth) : 15
  );

  const outerListPageQs = searchParams.get("page");
  useEffect(() => {
    const n = parsePageQueryString(outerListPageQs);
    setPage((prev) => (prev === n ? prev : n));
  }, [outerListPageQs]);

  const pageInUrl = searchParams.get("page");
  useEffect(() => {
    if (page <= 1) {
      if (!pageInUrl) return;
    } else if (pageInUrl === String(page)) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) next.delete("page");
        else next.set("page", String(page));
        return next;
      },
      { replace: true }
    );
  }, [page, pageInUrl, setSearchParams]);

  const [pageItems, setPageItems] = useState<HomeSeriesFolderItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
  });
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const resolveOwnerTypeForApi = useCallback((): number | undefined => {
    if (!isAdminOrSuperAdmin()) {
      return getDefaultOwnerType();
    }
    if (scope === "team") return 1;
    return 2;
  }, [isAdminOrSuperAdmin, scope]);

  const resolveSortForApi = useCallback((): number => {
    void timeSort;
    return 1;
  }, [timeSort]);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      const ownerType = resolveOwnerTypeForApi();
      const params: Series.GetSeriesListParams = {
        pageNo: page,
        pageSize,
        sort: resolveSortForApi(),
      };
      if (ownerType !== undefined) {
        params.ownerType = ownerType;
      }
      if (searchQuery.trim()) {
        params.name = searchQuery.trim();
      }

      const seriesData = await getSeriesListApi(params);
      const list = (seriesData.data?.list || []) as Record<string, unknown>[];
      const total = seriesData.data?.total || 0;

      const items = list.map(mapListItemToFolder).filter((it) => it.id > 0);
      setPageItems(items);

      const pages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
      const nextPagination = {
        total,
        pages,
        has_next: page * pageSize < total,
        has_prev: page > 1,
      };
      setPagination(nextPagination);
      paginationRef.current = nextPagination;
    } catch {
      message.error("获取剧集列表失败");
      setPageItems([]);
      const empty = { total: 0, pages: 0, has_next: false, has_prev: false };
      setPagination(empty);
      paginationRef.current = empty;
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchQuery, resolveOwnerTypeForApi, resolveSortForApi]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  /** 登录后 globalStore 写入/合并 userInfo 晚于首屏请求时，避免普通用户误用 ownerType=1 再拉一次 */
  useEffect(() => {
    const userInfoFingerprint = () => {
      const u = globalStore.getUserInfo();
      if (!u) return "";
      const roles = Array.isArray(u.roles) ? u.roles : [];
      const roleKeys = roles
        .map((x: unknown) =>
          typeof x === "string"
            ? x
            : String(
                (x as { role_key?: string; code?: string })?.role_key ??
                  (x as { code?: string }).code ??
                  ""
              )
        )
        .filter(Boolean)
        .join(",");
      return `${String(u.id ?? (u as { userId?: unknown }).userId ?? "")}|${roleKeys}|${String(u.role ?? "")}`;
    };
    const roleFingerprint = () => {
      const r = UserRoleManager.getCurrentUserRole();
      if (!r) return "null";
      return `${Number(r.isAdmin)}${Number(r.isAssessor)}${Number(r.isOperator)}${Number(r.isUser)}`;
    };
    const snap = () => `${userInfoFingerprint()}#${roleFingerprint()}`;
    let prev = snap();
    return globalStore.subscribe(() => {
      const next = snap();
      if (next !== prev) {
        prev = next;
        void refreshRef.current();
      }
    });
  }, []);

  useEffect(() => {
    const updatePageSize = () => {
      const newPageSize = getHomeSeriesListPageSize(window.innerWidth);
      setPageSize((prev) => {
        if (newPageSize !== prev) {
          setPage(1);
          return newPageSize;
        }
        return prev;
      });
    };
    updatePageSize();
    window.addEventListener("resize", updatePageSize);
    return () => window.removeEventListener("resize", updatePageSize);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    refresh();
  }, [location.pathname, refresh]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInputValue(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
      debounceTimerRef.current = null;
    }, 500);
  }, []);

  const setTimeSortAndReset = useCallback((v: HomeTimeSortOption) => {
    setTimeSort(v);
    setPage(1);
  }, []);

  const setScopeAndReset = useCallback((v: HomeScopeOption) => {
    setScope(v);
    setPage(1);
  }, []);

  const goPrev = useCallback(() => {
    if (paginationRef.current.has_prev) {
      setPage((p) => Math.max(1, p - 1));
    }
  }, []);

  const goNext = useCallback(() => {
    if (paginationRef.current.has_next) {
      setPage((p) => p + 1);
    }
  }, []);

  const pageCount = pagination.total === 0 ? 1 : Math.max(1, pagination.pages);

  return {
    searchInputValue,
    handleSearchChange,
    timeSort,
    setTimeSort: setTimeSortAndReset,
    scope,
    setScope: setScopeAndReset,
    showScopeFilter: isAdminOrSuperAdmin(),
    page,
    pageSize,
    pageCount,
    pageItems,
    totalCount: pagination.total,
    hasPrev: pagination.has_prev,
    hasNext: pagination.has_next,
    isLoading,
    refresh,
    goPrev,
    goNext,
  };
};
